/* © 2014 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, June 19, 2014
 * For information or permission request, email info@aetonixsystems.com
 */

var xtend = require("xtend");
var Promise = require("promise");
var asap = require("asap");
var mapObject = require("map-object");
var assign = require("object-assign");

/**
 * @module models
 */

module.exports = make_model;

make_model.Model = BaseModel;

make_model.makeExtender = makeExtender;
make_model.makeRecalculator = makeRecalculator;

function make_model(settings) {
	function Model(api, dispatcher, config) {
		if (!(this instanceof Model))
			return new Model(api, dispatcher, config);
		BaseModel.apply(this, arguments);
	}

	Model.prototype = assign(Object.create(BaseModel.prototype), settings);

	return Model;
}

/**
 * Model constructors take in external state, (api/dispatcher) and an arbitrary config
 * @param {Api} api                 The ae-api instance to use for the model
 * @param {EventEmitter} dispatcher The event emitter to use as the dispatcher, should handle MQTT messages as well
 * @param {Any} config              Whatever configuration that specific model type requires
 */
function BaseModel(api, dispatcher, config) {
	this.destroyed = false;
	this.data = undefined;
	this.config = config;
	this.api = api;
	this.dispatcher = dispatcher;
	this.tracker = dispatcher.createTracker();

	listen_on_new_listeners.call(this);
	Promise.resolve(this.init())
		.then(process_triggers.bind(this))
		.then(process_listeners.bind(this))
		.catch(console.error.bind(console));
}

/**
 * The prototype object for the Model.  It contains the properties: 'config', 'api',
 * 'distpatcher', 'tracker', and 'data'.  Several methods are also outlined.
 * @type {Object}
 */
BaseModel.prototype = {
	config: undefined, // The configuration for the model, passed into the constructor `Object`
	api: undefined, // The ae-api instance this model should use `Object`
	dispatcher: undefined, // The dispatcher that this model should use (handles mqtt as well) `EventEmitter`
	tracker: undefined, // A dispatcher tracker for keeping track of this model's listeners
	data: undefined, // The the current state of the model.
	destroyed: undefined, // Whether this model is destroyed

	init: init,
	uninit: uninit,
	listeners: listeners,
	triggers: triggers,

	destroy: destroy,
	push: push,
	dispatch: dispatch,
	emit: emit,
	on: on,
	extend: extend
};

/**
 * Generates the initial state for this model, listens to any events it needs from the dispatcher.
 * When the state is created it should be set with `.push(data)`
 * @return {Promise} Must resolve to the initial state for the model
 */
function init() {
	return Promise.resolve(this.push());
}

/**
 * Gets called to get rid of any extra data outside of listeners and the state
 * @return {Promise} Must resolve once all listeners are removed
 */
function uninit() {
	return Promise.resolve();
}

/**
 * Retruns a map of events to listen on the dispatcher
 * This can be overwritten by models to simplifiy event listening
 * The event listeners are automatically bound to `this`
 * @return {Object} The map of listeners to bind to
 */
function listeners() {
	return {};
}

/**
 * Returns a map of triggers to listen on
 * This can be overwritten by models to simplifiy trigger event listening
 * @return {Object} Map of action:listeners, action has this.name:trigger prepended
 */
function triggers() {
	return {};
}

/**
 * Destroys the model, removes all listeners, deletes data
 * @return {Promise} Resolves once everything is destroyed
 */
function destroy() {
	if (this.destroyed) return Promise.resolve();
	return Promise.resolve(this)
		.then(this.uninit.bind(this))
		.then(function () {
			this.tracker.off();
			this.data = null;
			this.destroyed = true;
		}.bind(this));
}

/**
 * Updates the state of the model and notifies all listeners
 * @param  {Any} data The state to update to
 * @return {Any}      Returns the data that was passed in for chaining
 */
function push(data) {
	this.data = data;
	this.dispatch("updated", data);
	return data;
}

/**
 * Dispatches a new event on its own namespace.
 * @param  {String} event Event to emit, gets prefixed with this.name+":"
 * @param  {Object} data  The payload for the event
 * @return {Model}        Returns self for chaining
 */
function dispatch(event, data) {
	var name = this.name;
	this.emit(name + ":" + event, data);
	return this;
}

/**
 * Emits an event to the dispatcher
 * @param  {String} event The event to emit
 * @param  {Object} data  The event payload to emit
 * @return {Model}        Returns self for chaining
 */
function emit(event, data, ...params) {
	var dispatcher = this.dispatcher;
	asap(function () {
		dispatcher.emit(event, data, ...params);
	});
}

/**
 * Sets a listener on an event.
 * @param  {string}   event Event name.
 * @param  {Function} fn    Function executed on event.
 */
function on(event, fn) {
	this.tracker.on(this.name + ":" + event, fn);
}

/**
 * Extends the current data and pushes
 * @param  {Object} data The data to extend with
 * @return {Object}      The new state of the model
 */
function extend(data) {
	return this.push(xtend(this.data, data));
}

/**
 * Gets invoked whenever there's a new listener for updates to this model.
 * Dispatches its current state, if it exists. 'setTimeout' ensures the process
 * is asynchronous.
 */
function handle_new_listener() {
	asap(function () {
		var data = this.data;
		if (data !== undefined) this.dispatch("updated", data);
	}.bind(this));
}

/**
 * Starts listening on new listeners for this model's state
 * The event that gets listened to is newlistener:this.name:updated
 */
function listen_on_new_listeners() {
	var event = "newlistener:" + this.name + ":updated";
	this.tracker.on(event, handle_new_listener.bind(this));
}

/**
 * Adds a new listener to the dispatcher
 * @param {Function} listener The callback to call for the event. Bound to `this`
 * @param {String} event    The event to listen on from the dispatcher
 */
function add_listener(listener, event) {
	this.tracker.on(event, listener.bind(this));
}

/**
 * Processes listeners for the model and adds them
 * Adds the listeners asynchronously
 */
function process_listeners() {
	var listenerMap = this.listeners();
	var add = add_listener.bind(this);
	asap(function () {
		mapObject(listenerMap, add);
	});
}

/**
 * Listens for a new trigger
 * @param {Function} listener The callbak to call when the trigger occures.
 * @param {String} action   The trigger name. this.name:trigger is prepended
 */
function add_trigger(listener, action) {
	this.tracker.on(this.name + ":trigger:" + action, listener.bind(this));
}

/**
 * Adds listeners for all the model's triggers
 */
function process_triggers() {
	var triggerMap = this.triggers();
	mapObject(triggerMap, add_trigger.bind(this));
}

function makeExtender(name) {
	return function (data) {
		var extension = {};
		try {
			extension[name] = data;
			this.extend(extension);
		} catch(e){
			console.error("Error extending", e, name);
		}
	};
}

function makeRecalculator(name) {
	return function(data){
		this["_" + name] = data;
		this.recalculate();
	};
}
